import React from 'react';
import { Info, AlertTriangle, CheckCircle, Lightbulb } from 'lucide-react';
import { useResponsive } from '../BlogUtils/ResponsiveUtils';

interface CalloutBoxProps {
  type: 'info' | 'warning' | 'success' | 'tip';
  title?: string;
  children: React.ReactNode;
}

const calloutConfig = {
  info: {
    icon: Info,
    borderColor: 'border-l-blue-500',
    bgColor: 'bg-blue-500/10',
    iconColor: 'text-blue-400',
    titleColor: 'text-blue-300',
  },
  warning: {
    icon: AlertTriangle,
    borderColor: 'border-l-yellow-500',
    bgColor: 'bg-yellow-500/10',
    iconColor: 'text-yellow-400',
    titleColor: 'text-yellow-300',
  },
  success: {
    icon: CheckCircle,
    borderColor: 'border-l-green-500',
    bgColor: 'bg-green-500/10',
    iconColor: 'text-green-400',
    titleColor: 'text-green-300',
  },
  tip: {
    icon: Lightbulb,
    borderColor: 'border-l-purple-500',
    bgColor: 'bg-purple-500/10',
    iconColor: 'text-purple-400',
    titleColor: 'text-purple-300',
  },
};

export const CalloutBox: React.FC<CalloutBoxProps> = ({ type, title, children }) => {
  const { isMobile } = useResponsive();
  const config = calloutConfig[type];
  const Icon = config.icon;

  return (
    <div className={`
      ${isMobile ? 'my-6 p-4 rounded-lg' : 'my-8 p-6 rounded-xl'} 
      backdrop-blur-xl border-l-4 border border-white/10 
      ${config.borderColor} ${config.bgColor}
      hover:bg-white/5 transition-all duration-300
    `}>
      <div className={`flex items-start ${isMobile ? 'gap-3' : 'gap-4'}`}>
        <div className={`flex-shrink-0 ${config.iconColor}`}>
          <Icon className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'}`} />
        </div>
        
        <div className="flex-1 min-w-0">
          {title && (
            <h4 className={`font-semibold ${isMobile ? 'mb-1.5 text-sm' : 'mb-2'} ${config.titleColor}`}>
              {title}
            </h4>
          )}
          
          <div className={`text-white/80 leading-relaxed ${isMobile ? 'text-sm' : ''}`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalloutBox;